import { UnifiedSearchIcon } from '@components/Icons/UnifiedSearch';
import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { availableSearchTypeOptions } from '@lib/utils/searchTypeOptions';
import TranscriptIcon from '@mui/icons-material/RecordVoiceOver';
import EyeIcon from '@mui/icons-material/RemoveRedEye';
import { SvgIcon } from '@mui/material';
import { SegmentType } from '@stores/Library/LibrarySearch';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactNode } from 'react';
import { TooltipBadge } from '../ResultTypeTooltip/ResultTypeTooltip';
import { SearchModeBadge } from '../SearchModeBadge/SearchModeBadge';
import css from './ResultTypeDrawer.module.scss';

const SEARCH_TYPE_ICONS: Record<SegmentType, ReactNode> = {
  visual: <EyeIcon className={css.optionIcon} />,
  transcript: <TranscriptIcon className={css.optionIcon} />,
  unified: (
    <SvgIcon className={css.optionIcon} sx={{ width: 20, height: 20 }}>
      <UnifiedSearchIcon />
    </SvgIcon>
  ),
};

type ResultTypeDrawerProps = {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  selectedType: SegmentType;
  onSelect: (type: SegmentType) => void;
  /**
   * Scope-wide warning/error ribbon shown at the top of the drawer.
   * Same treatment as the desktop tooltip's ribbon — see
   * `SearchModeBadge`. Pass `null` to suppress.
   */
  scopeBadge: TooltipBadge | null;
  /**
   * When true, Visual and Unified options render disabled (dimmed,
   * no-op tap). Selected-wins rule still applies: if one of them is
   * currently the selected mode (user was in that mode before
   * changing scope), it keeps the selected look.
   */
  disableVisualAndUnified: boolean;
};

export const ResultTypeDrawer = observer(function ResultTypeDrawer({
  open,
  onClose,
  onOpen,
  selectedType,
  onSelect,
  scopeBadge,
  disableVisualAndUnified,
}: ResultTypeDrawerProps) {
  // observer so the option list stays reactive to the unified-search debug flag
  // read inside availableSearchTypeOptions().
  const options = availableSearchTypeOptions();

  return (
    <MobileDrawer open={open} onClose={onClose} onOpen={onOpen} frosted>
      <SearchModeBadge badge={scopeBadge} className={css.drawerBadge} />
      <div className={css.options}>
        {options.map((option) => {
          const isAffectedByScope =
            disableVisualAndUnified && (option.type === 'visual' || option.type === 'unified');
          const isSelected = selectedType === option.type;
          // Selected-wins: a disabled option that happens to be the
          // current mode still shows its selected look so the user
          // can see where they are.
          const dimmed = isAffectedByScope && !isSelected;
          const clickable = !isAffectedByScope || isSelected;

          return (
            <div
              key={option.type}
              className={classNames(css.option, {
                [css.selected]: isSelected,
                [css.dimmed]: dimmed,
              })}
              onClick={
                clickable
                  ? () => {
                      onSelect(option.type);
                      onClose();
                    }
                  : undefined
              }
            >
              <div className={css.optionHeader}>
                {SEARCH_TYPE_ICONS[option.type]}
                <span className={css.optionTitle}>{option.title}</span>
              </div>
              <div className={css.examplePrompt}>
                <span className={css.exampleLabel}>Example prompt: </span>
                {option.examplePrompt.map((part, i) => (
                  <span key={i} className={part.type === 'incorrect' ? css.incorrect : css.correct}>
                    {part.text}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </MobileDrawer>
  );
});
